import React, { useEffect, useState } from "react";
import { api, formatBRL } from "../lib/api";
import { X, Loader2, Layers, FileText, Handshake } from "lucide-react";
import { toast } from "sonner";

export default function NewInstallmentContractModal({
  onClose,
  onCreated,
  prefilledClientId,
  prefilledKind,            // "parcelado" | "acordo"
  prefilledAgreedAmount,    // number
  prefilledOriginalContractId,  // str
  prefilledAcordoCapitalBase,   // number
  prefilledAcordoLucroTotal,    // number
  prefilledAgreementReason, // str
}) {
  // Common
  const [kind, setKind] = useState(prefilledKind || "parcelado");
  const [saving, setSaving] = useState(false);
  const [capitalSource, setCapitalSource] = useState("socio");
  const [clientId, setClientId] = useState(prefilledClientId || "");
  const [clients, setClients] = useState([]);
  const [socioId, setSocioId] = useState("");
  const [socios, setSocios] = useState([]);
  const [indicatorId, setIndicatorId] = useState("");
  const [indicators, setIndicators] = useState([]);
  const [installmentsCount, setInstallmentsCount] = useState(4);
  const [frequency, setFrequency] = useState("weekly");
  const [firstDueDate, setFirstDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [observations, setObservations] = useState("");

  // Parcelado
  const [capital, setCapital] = useState("");
  const [interestPercent, setInterestPercent] = useState("");

  // Acordo
  const [agreedAmount, setAgreedAmount] = useState(prefilledAgreedAmount ? String(prefilledAgreedAmount) : "");
  const [originalContractId, setOriginalContractId] = useState(prefilledOriginalContractId || "");
  const [acordoCapitalBase, setAcordoCapitalBase] = useState(prefilledAcordoCapitalBase ? String(prefilledAcordoCapitalBase) : "");
  const [acordoLucroTotal, setAcordoLucroTotal] = useState(prefilledAcordoLucroTotal ? String(prefilledAcordoLucroTotal) : "");
  const [agreementReason, setAgreementReason] = useState(prefilledAgreementReason || "");

  useEffect(() => {
    (async () => {
      try {
        const [c, s, i] = await Promise.all([
          api.get("/clients-extended", { params: { limit: 500 } }).catch(() => api.get("/clients")),
          api.get("/socios"),
          api.get("/indicators"),
        ]);
        setClients(Array.isArray(c.data) ? c.data : (c.data?.items || []));
        setSocios(Array.isArray(s.data) ? s.data : []);
        setIndicators(Array.isArray(i.data) ? i.data : []);
      } catch (_) {
        toast.error("Falha ao carregar listas");
      }
    })();
  }, []);

  // Preview cálculo
  const capNum = Number(capital) || 0;
  const intNum = Number(interestPercent) || 0;
  const totalParc = capNum * (1 + intNum / 100);
  const valorParcelaParc = installmentsCount > 0 ? totalParc / installmentsCount : 0;

  const agreedNum = Number(agreedAmount) || 0;
  const capBaseNum = Number(acordoCapitalBase) || 0;
  const lucroNum = Number(acordoLucroTotal) || 0;
  const valorParcelaAco = installmentsCount > 0 ? agreedNum / installmentsCount : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        kind,
        client_id: clientId,
        socio_id: capitalSource === "empresa" ? null : socioId,
        capital_source: capitalSource,
        indicator_id: indicatorId || null,
        installments_count: Number(installmentsCount),
        frequency,
        first_due_date: firstDueDate,
        observations,
      };
      if (kind === "parcelado") {
        body.capital = Number(capital);
        body.interest_percent = Number(interestPercent);
      } else {
        body.agreed_amount = Number(agreedAmount);
        body.agreement_reason = agreementReason;
        if (originalContractId) body.original_contract_id = originalContractId;
        if (acordoCapitalBase !== "") body.acordo_capital_base = Number(acordoCapitalBase);
        if (acordoLucroTotal !== "") body.acordo_lucro_total = Number(acordoLucroTotal);
      }
      await api.post("/installment-contracts", body);
      toast.success(`${kind === "parcelado" ? "Parcelado" : "Acordo"} criado com sucesso`);
      onCreated && onCreated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao criar contrato");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="new-installment-modal">
      <div className="fc-card w-full max-w-2xl my-8 p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-1.5">
              <Layers size={11} /> Novo
            </div>
            <h2 className="font-display text-2xl font-bold mt-1">Parcelado ou Acordo</h2>
          </div>
          <button onClick={onClose} className="fc-btn-ghost" data-testid="close-installment-modal">
            <X size={16} />
          </button>
        </div>

        {/* Origem do capital */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            type="button"
            onClick={() => setCapitalSource("socio")}
            data-testid="cs-socio"
            className={`p-3 rounded-md border text-left transition-colors ${
              capitalSource === "socio" ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#262626]"
            }`}
          >
            <div className="text-sm font-semibold">Sócio Investidor</div>
            <div className="text-[11px] text-[#A3A3A3]">Capital de sócio externo · split 50/50</div>
          </button>
          <button
            type="button"
            onClick={() => setCapitalSource("empresa")}
            data-testid="cs-empresa"
            className={`p-3 rounded-md border text-left transition-colors ${
              capitalSource === "empresa" ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#262626]"
            }`}
          >
            <div className="text-sm font-semibold">Capital Próprio</div>
            <div className="text-[11px] text-[#A3A3A3]">Capital da empresa · 100% lucro empresa</div>
          </button>
        </div>

        {/* Kind selector */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button
            type="button"
            onClick={() => setKind("parcelado")}
            data-testid="kind-parcelado"
            className={`p-3 rounded-md border text-left transition-colors ${
              kind === "parcelado" ? "border-blue-500/40 bg-blue-500/10" : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#262626]"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText size={14} className="text-blue-300" />
              <div className="text-sm font-semibold">Parcelado</div>
            </div>
            <div className="text-[11px] text-[#A3A3A3]">Capital + juros divididos em parcelas iguais</div>
          </button>
          <button
            type="button"
            onClick={() => setKind("acordo")}
            data-testid="kind-acordo"
            className={`p-3 rounded-md border text-left transition-colors ${
              kind === "acordo" ? "border-violet-500/40 bg-violet-500/10" : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#262626]"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Handshake size={14} className="text-violet-300" />
              <div className="text-sm font-semibold">Acordo</div>
            </div>
            <div className="text-[11px] text-[#A3A3A3]">Renegociação de dívida (com ou sem lucro)</div>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#737373]">Cliente</span>
              <select
                className="fc-input mt-1"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                required
                data-testid="select-client"
              >
                <option value="">Selecione...</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#737373]">
                Sócio responsável {capitalSource === "empresa" && <span className="text-emerald-300">(N/A - Capital Próprio)</span>}
              </span>
              <select
                className="fc-input mt-1"
                value={socioId}
                onChange={(e) => setSocioId(e.target.value)}
                required={capitalSource === "socio"}
                disabled={capitalSource === "empresa"}
                data-testid="select-socio"
              >
                <option value="">Selecione...</option>
                {socios.filter((s) => s.status === "ativo").map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#737373]">Indicador (opcional)</span>
              <select
                className="fc-input mt-1"
                value={indicatorId}
                onChange={(e) => setIndicatorId(e.target.value)}
                data-testid="select-indicator"
              >
                <option value="">— sem indicador —</option>
                {indicators.map((i) => (
                  <option key={i.id} value={i.id}>{i.name} ({i.code})</option>
                ))}
              </select>
              <span className="text-[10.5px] text-[#737373] mt-1 block">Comissão de 5% sobre o capital (gerada 1× na criação)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-[#737373]">Parcelas</span>
                <input
                  type="number"
                  min="1"
                  max="120"
                  className="fc-input mt-1"
                  value={installmentsCount}
                  onChange={(e) => setInstallmentsCount(e.target.value)}
                  required
                  data-testid="input-installments"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-[#737373]">Frequência</span>
                <select
                  className="fc-input mt-1"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  data-testid="select-frequency"
                >
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensal</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#737373]">1ª parcela</span>
              <input
                type="date"
                className="fc-input mt-1"
                value={firstDueDate}
                onChange={(e) => setFirstDueDate(e.target.value)}
                required
                data-testid="input-first-due"
              />
            </label>
          </div>

          {/* Campos por tipo */}
          {kind === "parcelado" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-md border border-blue-500/20 bg-blue-500/5">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-blue-300">Capital (R$)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="fc-input mt-1"
                  value={capital}
                  onChange={(e) => setCapital(e.target.value)}
                  required
                  data-testid="input-capital"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-blue-300">Juros (%)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="fc-input mt-1"
                  value={interestPercent}
                  onChange={(e) => setInterestPercent(e.target.value)}
                  required
                  data-testid="input-interest"
                />
              </label>
              {capNum > 0 && intNum >= 0 && installmentsCount > 0 && (
                <div className="sm:col-span-2 text-[12px] text-[#A3A3A3] flex items-center justify-between pt-2 border-t border-blue-500/20">
                  <span>Valor total: <strong className="text-white">{formatBRL(totalParc)}</strong></span>
                  <span>{installmentsCount}x de <strong className="text-[#D4AF37]">{formatBRL(valorParcelaParc)}</strong></span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 p-4 rounded-md border border-violet-500/20 bg-violet-500/5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-violet-300">Valor acordado (R$)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="fc-input mt-1"
                    value={agreedAmount}
                    onChange={(e) => setAgreedAmount(e.target.value)}
                    required
                    data-testid="input-agreed-amount"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-violet-300">Contrato original (opcional)</span>
                  <input
                    type="text"
                    className="fc-input mt-1"
                    value={originalContractId}
                    onChange={(e) => setOriginalContractId(e.target.value)}
                    placeholder="ID do contrato normal"
                    data-testid="input-original-contract"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-violet-300">Capital base (R$)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="fc-input mt-1"
                    value={acordoCapitalBase}
                    onChange={(e) => setAcordoCapitalBase(e.target.value)}
                    placeholder="Obrigatório se sem contrato original"
                    data-testid="input-acordo-capital-base"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-violet-300">Lucro total (R$)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="fc-input mt-1"
                    value={acordoLucroTotal}
                    onChange={(e) => setAcordoLucroTotal(e.target.value)}
                    placeholder="0 = sem lucro"
                    data-testid="input-acordo-lucro"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-violet-300">Motivo do acordo</span>
                <input
                  type="text"
                  className="fc-input mt-1"
                  value={agreementReason}
                  onChange={(e) => setAgreementReason(e.target.value)}
                  placeholder="Ex: renegociação por atraso"
                  data-testid="input-agreement-reason"
                />
              </label>
              {agreedNum > 0 && installmentsCount > 0 && (
                <div className="text-[12px] text-[#A3A3A3] flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-violet-500/20">
                  <span>Cap+Lucro: <strong className="text-white">{formatBRL(capBaseNum + lucroNum)}</strong> {(capBaseNum + lucroNum) !== agreedNum && <span className="text-red-300">(≠ acordado)</span>}</span>
                  <span>{installmentsCount}x de <strong className="text-[#D4AF37]">{formatBRL(valorParcelaAco)}</strong></span>
                </div>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#737373]">Observações</span>
            <textarea
              className="fc-input mt-1"
              rows="2"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              data-testid="input-observations"
            />
          </label>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#1f1f1f]">
            <button type="button" onClick={onClose} className="fc-btn-ghost" data-testid="cancel-installment">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="fc-btn-gold flex items-center gap-2" data-testid="submit-installment">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Salvando…</> : <>Criar {kind}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
